import React, { useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { ArrowLeft, RefreshCw, Search, X } from "react-feather";
import { Link } from "react-router";
import Select from "react-select";
import { format, startOfDay, endOfDay } from "date-fns";
import { useGet } from "../../api";
import { useAuth } from "../../hooks";
import {
  IUser,
  IWorkPipelineAuditEvent,
  WORK_PIPELINE_STAGE_LABELS,
  WorkPipelineAuditEventType,
} from "../../interfaces";
import { DomainChanges } from "./domain-changes";

interface IAuditResponse {
  results: IWorkPipelineAuditEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const PAGE_SIZE = 50;

// Partial so adding an audit event type degrades to a derived label rather
// than breaking the build; only overrides live here.
const EVENT_LABEL: Partial<Record<WorkPipelineAuditEventType, string>> = {
  [WorkPipelineAuditEventType.StageChanged]: "Stage changed",
  [WorkPipelineAuditEventType.Claimed]: "Claimed",
  [WorkPipelineAuditEventType.Released]: "Released",
  [WorkPipelineAuditEventType.OutcomeRecorded]: "Outcome",
  [WorkPipelineAuditEventType.ManualMove]: "Manual move",
  [WorkPipelineAuditEventType.AutoReset]: "Auto reset",
  [WorkPipelineAuditEventType.SnapshotAdded]: "Snapshot added",
  [WorkPipelineAuditEventType.SessionRunFailed]: "Session run failed",
  [WorkPipelineAuditEventType.Reopened]: "Reopened",
  [WorkPipelineAuditEventType.EntityEdited]: "Edited",
  [WorkPipelineAuditEventType.BrandStatusSet]: "Brand status",
  [WorkPipelineAuditEventType.BlockProposed]: "Block proposed",
  [WorkPipelineAuditEventType.BlockApproved]: "Block approved",
  [WorkPipelineAuditEventType.BlockRejected]: "Block rejected",
  [WorkPipelineAuditEventType.BlockRestored]: "Restored",
  [WorkPipelineAuditEventType.HuntRecorded]: "Code hunt",
  [WorkPipelineAuditEventType.UrgentSet]: "Marked urgent",
  [WorkPipelineAuditEventType.UrgentCleared]: "Urgent cleared",
};

/** Turns "session-run-failed" into "Session run failed". */
const eventLabel = (t: WorkPipelineAuditEventType): string =>
  EVENT_LABEL[t] ??
  String(t)
    .replace(/-/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const EVENT_VARIANT: Partial<Record<WorkPipelineAuditEventType, string>> = {
  [WorkPipelineAuditEventType.StageChanged]: "primary",
  [WorkPipelineAuditEventType.Claimed]: "info",
  [WorkPipelineAuditEventType.Released]: "secondary",
  [WorkPipelineAuditEventType.OutcomeRecorded]: "success",
  [WorkPipelineAuditEventType.ManualMove]: "warning",
  [WorkPipelineAuditEventType.AutoReset]: "secondary",
  [WorkPipelineAuditEventType.SnapshotAdded]: "info",
  [WorkPipelineAuditEventType.SessionRunFailed]: "danger",
  [WorkPipelineAuditEventType.Reopened]: "primary",
  [WorkPipelineAuditEventType.EntityEdited]: "secondary",
  [WorkPipelineAuditEventType.BrandStatusSet]: "dark",
  [WorkPipelineAuditEventType.BlockProposed]: "warning",
  [WorkPipelineAuditEventType.BlockApproved]: "dark",
  [WorkPipelineAuditEventType.BlockRejected]: "secondary",
  [WorkPipelineAuditEventType.BlockRestored]: "info",
  [WorkPipelineAuditEventType.HuntRecorded]: "secondary",
  [WorkPipelineAuditEventType.UrgentSet]: "danger",
  [WorkPipelineAuditEventType.UrgentCleared]: "secondary",
};

const eventVariant = (t: WorkPipelineAuditEventType): string =>
  EVENT_VARIANT[t] ?? "secondary";

const todayStart = () => format(startOfDay(new Date()), "yyyy-MM-dd");
const todayEnd = () => format(endOfDay(new Date()), "yyyy-MM-dd");

const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: 31, fontSize: "0.875rem" }),
  menu: (base: any) => ({ ...base, fontSize: "0.875rem" }),
};

export const WorkPipelineAudit: React.FC = () => {
  const { data: users } = useGet<IUser[]>("qa-lists/users/all", true);
  const { data: refCountries } = useGet<string[]>(
    "kindred/reference/countries",
    true
  );

  // ── Draft filters ─────────────────────────────────────────
  const [domainDraft, setDomainDraft] = useState("");
  const [countryDraft, setCountryDraft] = useState("");
  const [actorDraft, setActorDraft] = useState("");
  const [eventTypeDraft, setEventTypeDraft] = useState("");
  const [dateFromDraft, setDateFromDraft] = useState(todayStart());
  const [dateToDraft, setDateToDraft] = useState(todayEnd());

  // ── Applied filters ───────────────────────────────────────
  const [domain, setDomain] = useState("");
  const [country, setCountry] = useState("");
  const [actor, setActor] = useState("");
  const [eventType, setEventType] = useState("");
  const [dateFrom, setDateFrom] = useState(todayStart());
  const [dateTo, setDateTo] = useState(todayEnd());
  const [page, setPage] = useState(1);
  const [view, setView] = useState<"events" | "changes">("events");
  const [refreshKey, setRefreshKey] = useState(0);

  const auditUrl = (() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("limit", String(PAGE_SIZE));
    if (domain) p.set("domain", domain);
    if (country) p.set("countryCode", country);
    if (actor) p.set("actor", actor);
    if (eventType) p.set("eventType", eventType);
    if (dateFrom) p.set("dateFrom", new Date(dateFrom).toISOString());
    if (dateTo) {
      // Include the full end day
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      p.set("dateTo", end.toISOString());
    }
    return `work-pipeline/audit?${p.toString()}`;
  })();

  const { data, loading, refetch } = useGet<IAuditResponse>(auditUrl, true);

  const applyFilters = () => {
    setPage(1);
    setDomain(domainDraft);
    setCountry(countryDraft);
    setActor(actorDraft);
    setEventType(eventTypeDraft);
    setDateFrom(dateFromDraft);
    setDateTo(dateToDraft);
  };

  const clearFilters = () => {
    setDomainDraft("");
    setCountryDraft("");
    setActorDraft("");
    setEventTypeDraft("");
    setDateFromDraft(todayStart());
    setDateToDraft(todayEnd());
    setDomain("");
    setCountry("");
    setActor("");
    setEventType("");
    setDateFrom(todayStart());
    setDateTo(todayEnd());
    setPage(1);
  };

  const hasActiveFilters = !!(domain || country || actor || eventType);

  const userOptions = (users ?? []).map((u) => ({
    value: u._id.toString(),
    label: u.username,
  }));

  const countryOptions = (refCountries ?? []).map((c) => ({
    value: c,
    label: c,
  }));

  const pag = data?.pagination;

  const stageLabel = (s: string | null) =>
    s ? ((WORK_PIPELINE_STAGE_LABELS as any)[s] ?? s) : "—";

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/work-pipeline"
            className="btn btn-sm btn-outline-secondary"
          >
            <ArrowLeft size={14} className="me-1" /> Pipeline
          </Link>
          <h4 className="mb-0">Pipeline Audit Log</h4>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            refetch();
            setRefreshKey((n) => n + 1);
          }}
          title="Reload the events and field changes"
        >
          <RefreshCw size={14} className="me-1" /> Refresh
        </Button>
      </div>

      {/* Filter bar */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="small mb-1">From</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={dateFromDraft}
                onChange={(e) => setDateFromDraft(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">To</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={dateToDraft}
                onChange={(e) => setDateToDraft(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Person</Form.Label>
              <Select
                options={userOptions}
                value={
                  actorDraft
                    ? (userOptions.find((o) => o.value === actorDraft) ?? null)
                    : null
                }
                onChange={(o) => setActorDraft(o?.value ?? "")}
                isClearable
                placeholder="Anyone…"
                styles={selectStyles}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Country</Form.Label>
              <Select
                options={countryOptions}
                value={
                  countryDraft
                    ? { value: countryDraft, label: countryDraft }
                    : null
                }
                onChange={(o) => setCountryDraft(o?.value ?? "")}
                isClearable
                placeholder="All…"
                styles={selectStyles}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Event</Form.Label>
              <Form.Select
                size="sm"
                value={eventTypeDraft}
                onChange={(e) => setEventTypeDraft(e.target.value)}
              >
                <option value="">All events</option>
                {Object.values(WorkPipelineAuditEventType).map((t) => (
                  <option key={t} value={t}>
                    {eventLabel(t)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Domain</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Search domain…"
                value={domainDraft}
                onChange={(e) => setDomainDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
              />
            </Col>
            <Col md={12} className="d-flex gap-2">
              <Button size="sm" variant="primary" onClick={applyFilters}>
                <Search size={14} className="me-1" /> Search
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                <X size={14} /> Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {domain && (
        <Nav
          variant="tabs"
          activeKey={view}
          onSelect={(k) => setView((k as "events" | "changes") ?? "events")}
        >
          <Nav.Item>
            <Nav.Link
              eventKey="events"
              title="Pipeline events: claims, stage moves, outcomes"
            >
              Pipeline events
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="changes"
              title="Every column change on this domain's coupons, deals, offers and brands, from any app"
            >
              Field changes on {domain}
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      {domain && view === "changes" ? (
        <Card>
          <DomainChanges
            domain={domain}
            // The page defaults to today; field changes want a longer look back unless dates were set.
            dateFrom={
              dateFrom === todayStart() && dateTo === todayEnd() ? "" : dateFrom
            }
            dateTo={
              dateFrom === todayStart() && dateTo === todayEnd() ? "" : dateTo
            }
            refreshKey={refreshKey}
          />
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <small className="text-muted">
              {pag ? `${pag.total} event${pag.total === 1 ? "" : "s"}` : ""}
            </small>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center p-4">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : !data?.results?.length ? (
              <div className="text-center text-muted p-4">
                No events for this period{hasActiveFilters ? " / filters" : ""}.
              </div>
            ) : (
              <Table
                hover
                className="mb-0 align-middle"
                style={{ fontSize: "0.85rem" }}
              >
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Domain</th>
                    <th>Country</th>
                    <th>Who</th>
                    <th>Event</th>
                    <th>From → To</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((ev) => (
                    <tr key={ev._id}>
                      <td className="text-nowrap text-muted">
                        {format(new Date(ev.createdAt), "HH:mm:ss")}
                        <div>
                          <small className="text-muted">
                            {format(new Date(ev.createdAt), "dd MMM")}
                          </small>
                        </div>
                      </td>
                      <td className="fw-semibold">{ev.domain}</td>
                      <td>
                        <Badge bg="light" text="dark">
                          {ev.countryCode}
                        </Badge>
                      </td>
                      <td>
                        <small>
                          {users?.find((user) => user._id === ev.actor)
                            ?.username ?? ev.actorName}
                        </small>
                      </td>
                      <td>
                        <Badge bg={eventVariant(ev.eventType)}>
                          {eventLabel(ev.eventType)}
                        </Badge>
                      </td>
                      <td className="text-nowrap">
                        {ev.fromStage || ev.toStage ? (
                          <small>
                            {ev.fromStage ? stageLabel(ev.fromStage) : "—"}
                            {" → "}
                            {ev.toStage ? stageLabel(ev.toStage) : "—"}
                          </small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{ev.detail || "—"}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>

          {pag && pag.totalPages > 1 && (
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Showing {(pag.page - 1) * pag.limit + 1}–
                {Math.min(pag.page * pag.limit, pag.total)} of {pag.total}
              </small>
              <div className="d-flex gap-2 align-items-center">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <small className="text-muted">
                  Page {pag.page} of {pag.totalPages}
                </small>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page >= pag.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </Card.Footer>
          )}
        </Card>
      )}
    </div>
  );
};
